import { useEffect, useState } from 'react';
import { Outlet, Link, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

import {
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ShieldCheck,
  BadgeCheck,
  Star,
  Flag,
  AlertTriangle,
  Building2,
  ChevronRight
} from 'lucide-react';
import Logo from '../components/Logo';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgencyLayout({ customAgency }) {
  const { slug: urlSlug } = useParams();
  const slug = customAgency ? customAgency.slug : urlSlug;
  const location = useLocation();
  const [agency, setAgency] = useState(customAgency || null);
  const [loading, setLoading] = useState(!customAgency);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const themeColor = agency?.theme_color || '#0EA5E9';

  useEffect(() => {
    fetchAgency();
  }, [slug]);

  const fetchAgency = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/agency/${slug}`);
      setAgency(response.data);
    } catch (err) {
      setError('Agency not found');
    } finally {
      setLoading(false);
    }
  };

  // Report Logic
  const { isCustomer, getAuthHeader } = useAuth();
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportForm, setReportForm] = useState({ reason: 'fraud', description: '' });

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setReportLoading(true);
    try {
      await axios.post(`${API_URL}/api/reports`, {
        target_id: agency.id,
        target_type: 'agency',
        ...reportForm
      }, { headers: getAuthHeader() });
      toast.success('Report submitted successfully');
      setShowReportModal(false);
      setReportForm({ reason: 'fraud', description: '' });
    } catch (error) {
      toast.error('Failed to submit report');
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-sky-500 animate-pulse mx-auto mb-4" />
          <p className="text-navy-600">Loading agency...</p>
        </div>
      </div>
    );
  }

  if (error || !agency) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 text-navy-300 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-navy-900 mb-2">Agency Not Found</h1>
          <p className="text-navy-600 mb-6">The agency you're looking for doesn't exist.</p>
          <Link to="/">
            <Button className="bg-navy-900 hover:bg-navy-800">
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const navItems = [
    { label: 'Home', path: `/agency/${slug}` },
    { label: 'Properties', path: `/agency/${slug}/properties` },
    { label: 'About', path: `/agency/${slug}/about` },
    { label: 'Contact', path: `/agency/${slug}/contact` },
  ];

  const isActive = (path) => {
    if (path === `/agency/${slug}`) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="agency-layout" style={{ '--agency-theme': themeColor }}>
      {/* Top Bar - Premium Minimalist */}
      <div className="text-navy-900/60 py-2.5 px-6 hidden lg:block border-b border-navy-50 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.15em]">
          <div className="flex items-center gap-8">
            <span className="flex items-center gap-2 hover:text-sky-600 transition-colors cursor-pointer">
              <Phone className="w-3.5 h-3.5" />
              {agency.phone}
            </span>
            <span className="flex items-center gap-2 hover:text-sky-600 transition-colors cursor-pointer">
              <Mail className="w-3.5 h-3.5" />
              {agency.email}
            </span>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full shadow-sm ring-1 ring-navy-50">
              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
              <span className="text-navy-900">Trust: {agency.trust_rating || 'Exclusive'}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-white rounded-full shadow-sm ring-1 ring-navy-50">
              <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
              <span className="text-navy-900">RERA: {agency.rera_number}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation - Floating Glass Effect */}
      <nav className="sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="glass shadow-[0_20px_50px_-20px_rgba(15,23,42,0.1)] rounded-[1.5rem] px-8 py-4 border border-white/50 backdrop-blur-xl flex items-center justify-between">
            {/* Logo */}
            <Link
              to={`/agency/${slug}`}
              className="flex items-center gap-4 group"
              data-testid="agency-logo"
            >
              {agency.logo_url ? (
                <div className="relative h-12 w-12 rounded-2xl overflow-hidden ring-2 ring-navy-50 group-hover:ring-sky-200 transition-all">
                  <img
                    src={agency.logo_url}
                    alt={agency.company_name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-12 w-12 bg-sky-50 rounded-2xl flex items-center justify-center group-hover:bg-sky-100 transition-all">
                  <Building2 className="w-7 h-7 text-sky-500" />
                </div>
              )}
              <span className="text-2xl font-heading font-bold text-navy-900 flex items-center gap-2.5 leading-none mb-1">
                {agency.company_name}
                {agency.verified && (
                  <BadgeCheck className="w-5 h-5 text-blue-500" />
                )}
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-10">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-bold uppercase tracking-widest transition-all relative group/link ${isActive(item.path)
                    ? 'text-sky-500'
                    : 'text-navy-600 hover:text-navy-900'
                    }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-sky-500 transition-all group-hover/link:w-full ${isActive(item.path) ? 'w-full' : ''}`} />
                </Link>
              ))}
              <div className="h-8 w-px bg-navy-100 mx-2" />
              <Link to={`/agency/${slug}/contact`}>
                <Button
                  className="bg-navy-900 hover:bg-navy-800 text-white rounded-full px-8 shadow-xl shadow-navy-200 transition-all hover:scale-105 active:scale-95"
                  data-testid="enquire-now-btn"
                >
                  Connect Now
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden rounded-2xl hover:bg-navy-50"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <AnimatePresence mode="wait">
                {mobileMenuOpen ? (
                  <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </Button>
          </div>

          {/* Mobile Nav */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="lg:hidden absolute left-6 right-6 top-[100px] z-50"
              >
                <div className="glass rounded-[2rem] p-8 shadow-2xl border border-white/50 backdrop-blur-2xl">
                  <div className="flex flex-col space-y-6">
                    {navItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`text-lg font-heading font-medium flex items-center justify-between group ${isActive(item.path)
                          ? 'text-sky-500'
                          : 'text-navy-900'
                          }`}
                      >
                        {item.label}
                        <ChevronRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${isActive(item.path) ? 'text-sky-400' : 'text-navy-200'}`} />
                      </Link>
                    ))}
                    <div className="pt-4">
                      <Link to={`/agency/${slug}/contact`} onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full bg-navy-900 text-white h-14 rounded-2xl text-lg font-heading">
                          Enquire Now
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      {/* Page Content */}
      <main>
        <Outlet context={{ agency }} />
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-12 px-6" data-testid="agency-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {agency.logo_url ? (
                  <img
                    src={agency.logo_url}
                    alt={agency.company_name}
                    className="h-8 w-auto object-contain brightness-0 invert"
                  />
                ) : (
                  <Building2 className="w-8 h-8 text-sky-400" />
                )}
                <span className="text-lg font-heading font-bold">{agency.company_name}</span>
              </div>
              <p className="text-navy-300 text-sm leading-relaxed">
                {agency.about || 'Your trusted real estate partner.'}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block text-navy-300 hover:text-white text-sm"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact Info</h4>
              <div className="space-y-3 text-sm text-navy-300">
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  {agency.address}
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {agency.phone}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {agency.email}
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-navy-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-navy-400">
              © {new Date().getFullYear()} {agency.company_name}. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-sm text-navy-400">
              <ShieldCheck className="w-4 h-4 text-green-400" />
              <span>RERA Registration: {agency.rera_number}</span>
              <span className="mx-2">|</span>
              <button
                onClick={() => {
                  if (!isCustomer) {
                    toast.error('Please login to report agencies');
                    return;
                  }
                  setShowReportModal(true);
                }}
                className="flex items-center gap-1 hover:text-red-400 transition-colors"
              >
                <Flag className="w-3 h-3" />
                Report Agency
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy-900">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Report Agency
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleReportSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Reason for reporting</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                value={reportForm.reason}
                onChange={e => setReportForm({ ...reportForm, reason: e.target.value })}
                required
              >
                <option value="fraud">Fraud / Scam</option>
                <option value="misleading">Misleading Information</option>
                <option value="spam">Spam / Duplicate</option>
                <option value="inappropriate">Inappropriate Behavior</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Please provide more details about this agency..."
                value={reportForm.description}
                onChange={e => setReportForm({ ...reportForm, description: e.target.value })}
                className="min-h-[100px]"
                required
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowReportModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                className="flex-1"
                disabled={reportLoading}
              >
                {reportLoading ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
